import { useQuery } from '@tanstack/react-query';
import { inventory } from '@/lib/supabase/inventory';
import { marketing } from '@/lib/supabase/marketing';
import { petpoojaOutlet } from '@/lib/petpooja-outlets';
import { useDashboard } from '@/hooks/useDashboardContext';
import {
  computeMetric,
  effectivePeriodRange,
  getCompareRange,
  toISTDateString,
  type DateRange,
  type Metric,
  type PeriodKey,
} from '@/lib/periods';
import { fmtISTDate } from '@/lib/format';

const DAY_MS = 86_400_000;

/**
 * Business-review metrics sourced from the Petpooja POS data in the Inventory
 * project (the fully-backfilled copy, not the partial real-time webhook feed):
 *  - public.petpooja_dsr_daily  → per outlet/day gross, net, discount, tax,
 *    by_channel (dine-in/pickup/delivery) and by_source (Zomato/Swiggy/POS),
 *    read via the `inventory` client (public schema).
 *  - marketing.pos_menu_monthly / pos_category_monthly → item & category
 *    revenue, read via the `marketing` client.
 * Definitions mirror the flax-outlets businessReport: gross = total_collection,
 * net = core_total, discountPct = discount / (net + discount).
 */

interface DsrRow {
  outlet_id: string;
  order_date: string;
  bills: number | string | null;
  covers: number | string | null;
  core_total: number | string | null;
  discount_total: number | string | null;
  tax_total: number | string | null;
  total_collection: number | string | null;
  by_channel: Record<string, { net?: number; bills?: number; total?: number }> | null;
  by_source: Record<string, { net?: number; bills?: number; total?: number }> | null;
}

export interface OutletReviewLine {
  outletId: string;
  name: string;
  city: string;
  gross: number;
  net: number;
  bills: number;
  aov: number;
  netMetric: Metric;
}

export interface MixRow {
  key: string;
  sales: number;
  sharePct: number;
}

export interface WeekRow {
  label: string;
  gross: number;
  net: number;
  online: number;
  onlinePct: number;
  wowPct: number | null;
}

export interface CityRow {
  city: string;
  gross: number;
  net: number;
  bills: number;
  netMetric: Metric;
}

export interface PosReview {
  brand: {
    gross: number;
    net: number;
    bills: number;
    discount: number;
    tax: number;
    discountPct: number;
    aov: number;
    grossMetric: Metric;
    netMetric: Metric;
    billsMetric: Metric;
    aovMetric: Metric;
  };
  outlets: OutletReviewLine[];
  cities: CityRow[];
  channelMix: MixRow[];
  aggregator: { zomato: number; swiggy: number; total: number; zomatoPct: number };
  perOutletSource: { outletId: string; name: string; zomato: number; swiggy: number }[];
  weekly: WeekRow[];
  current: DateRange;
  previous: DateRange;
}

interface OutletAgg {
  gross: number;
  net: number;
  bills: number;
  discount: number;
  tax: number;
  zomato: number;
  swiggy: number;
}

interface Rollup {
  outlets: Map<string, OutletAgg>;
  channel: Map<string, number>;
  totals: { gross: number; net: number; core: number; bills: number; discount: number; tax: number };
}

const n = (v: unknown): number => {
  const x = Number(v ?? 0);
  return Number.isFinite(x) ? x : 0;
};

async function fetchDsr(range: DateRange): Promise<DsrRow[]> {
  const start = toISTDateString(range.start);
  const end = toISTDateString(new Date(range.end.getTime() - 1));
  const { data, error } = await inventory
    .from('petpooja_dsr_daily')
    .select(
      'outlet_id, order_date, bills, covers, core_total, discount_total, tax_total, total_collection, by_channel, by_source',
    )
    .gte('order_date', start)
    .lte('order_date', end);
  if (error) throw error;
  return (data ?? []) as unknown as DsrRow[];
}

function rollup(rows: DsrRow[]): Rollup {
  const outlets = new Map<string, OutletAgg>();
  const channel = new Map<string, number>();
  const totals = { gross: 0, net: 0, core: 0, bills: 0, discount: 0, tax: 0 };

  for (const r of rows) {
    // Net sales = item revenue after discounts. by_channel/by_source carry the
    // pre-discount item value (core) per split, which we scale down later.
    const core = n(r.core_total);
    const discount = n(r.discount_total);
    const net = core - discount;
    const gross = n(r.total_collection);
    const bills = n(r.bills);
    const tax = n(r.tax_total);

    totals.gross += gross;
    totals.net += net;
    totals.core += core;
    totals.bills += bills;
    totals.discount += discount;
    totals.tax += tax;

    const agg = outlets.get(r.outlet_id) ?? {
      gross: 0,
      net: 0,
      bills: 0,
      discount: 0,
      tax: 0,
      zomato: 0,
      swiggy: 0,
    };
    agg.gross += gross;
    agg.net += net;
    agg.bills += bills;
    agg.discount += discount;
    agg.tax += tax;

    for (const [k, v] of Object.entries(r.by_channel ?? {})) {
      channel.set(k, (channel.get(k) ?? 0) + n(v?.net));
    }
    for (const [k, v] of Object.entries(r.by_source ?? {})) {
      const key = k.toLowerCase();
      if (key === 'zomato') agg.zomato += n(v?.net);
      else if (key === 'swiggy') agg.swiggy += n(v?.net);
    }
    outlets.set(r.outlet_id, agg);
  }

  return { outlets, channel, totals };
}

/** Bucket DSR rows into 7-day weeks anchored to the period start, with WoW growth. */
function buildWeekly(rows: DsrRow[], current: DateRange): WeekRow[] {
  const startStr = toISTDateString(current.start);
  const startMs = Date.parse(`${startStr}T00:00:00Z`);
  const lastMs = current.end.getTime() - DAY_MS; // last included day (IST midnight instant)
  if (!Number.isFinite(startMs)) return [];

  const buckets = new Map<number, { gross: number; core: number; discount: number; dineIn: number }>();
  for (const r of rows) {
    const dayMs = Date.parse(`${r.order_date}T00:00:00Z`);
    if (Number.isNaN(dayMs)) continue;
    const weekIdx = Math.floor((dayMs - startMs) / (7 * DAY_MS));
    if (weekIdx < 0) continue;
    const b = buckets.get(weekIdx) ?? { gross: 0, core: 0, discount: 0, dineIn: 0 };
    b.gross += n(r.total_collection);
    b.core += n(r.core_total);
    b.discount += n(r.discount_total);
    for (const [k, v] of Object.entries(r.by_channel ?? {})) {
      if (k.toLowerCase() === 'dine in') b.dineIn += n(v?.net);
    }
    buckets.set(weekIdx, b);
  }

  // Emit every week spanning the period, even empty ones, so gaps in the feed
  // (e.g. a week with no orders) still show on the axis instead of vanishing.
  const lastIdx = Math.max(0, Math.floor((lastMs - startMs) / (7 * DAY_MS)));
  const out: WeekRow[] = [];
  let prevNet: number | null = null;
  for (let idx = 0; idx <= lastIdx; idx++) {
    const b = buckets.get(idx) ?? { gross: 0, core: 0, discount: 0, dineIn: 0 };
    const wsMs = startMs + idx * 7 * DAY_MS;
    const weMs = Math.min(wsMs + 6 * DAY_MS, lastMs);
    const label = `${fmtISTDate(new Date(wsMs), 'd MMM')} – ${fmtISTDate(new Date(weMs), 'd MMM')}`;
    const net = b.core - b.discount;
    const online = Math.max(0, b.core - b.dineIn);
    out.push({
      label,
      gross: b.gross,
      net,
      online,
      onlinePct: b.core > 0 ? (online / b.core) * 100 : 0,
      wowPct: prevNet != null && prevNet > 0 ? ((net - prevNet) / prevNet) * 100 : null,
    });
    prevNet = net;
  }
  return out;
}

export function usePosReview({ period }: { period: PeriodKey }) {
  const { customRange } = useDashboard();
  const current = effectivePeriodRange(period, customRange);
  const previous = getCompareRange(current, 'previous');

  const query = useQuery<PosReview>({
    queryKey: ['pos-review', current.start.toISOString(), previous.start.toISOString()],
    queryFn: async () => {
      const [curRows, prevRows] = await Promise.all([fetchDsr(current), fetchDsr(previous)]);
      const cur = rollup(curRows);
      const prev = rollup(prevRows);

      const brandAov = (t: Rollup['totals']) => (t.bills > 0 ? t.net / t.bills : 0);
      const brand: PosReview['brand'] = {
        gross: cur.totals.gross,
        net: cur.totals.net,
        bills: cur.totals.bills,
        discount: cur.totals.discount,
        tax: cur.totals.tax,
        discountPct:
          cur.totals.net + cur.totals.discount > 0
            ? (cur.totals.discount / (cur.totals.net + cur.totals.discount)) * 100
            : 0,
        aov: brandAov(cur.totals),
        grossMetric: computeMetric(cur.totals.gross, prev.totals.gross),
        netMetric: computeMetric(cur.totals.net, prev.totals.net),
        billsMetric: computeMetric(cur.totals.bills, prev.totals.bills),
        aovMetric: computeMetric(brandAov(cur.totals), brandAov(prev.totals)),
      };

      const outlets: OutletReviewLine[] = Array.from(cur.outlets.entries())
        .map(([outletId, a]) => {
          const meta = petpoojaOutlet(outletId);
          const prevNet = prev.outlets.get(outletId)?.net ?? 0;
          return {
            outletId,
            name: meta.name,
            city: meta.city,
            gross: a.gross,
            net: a.net,
            bills: a.bills,
            aov: a.bills > 0 ? a.net / a.bills : 0,
            netMetric: computeMetric(a.net, prevNet),
          };
        })
        .filter((o) => o.net > 0)
        .sort((a, b) => b.net - a.net);

      const cityAgg = new Map<string, { gross: number; net: number; bills: number; prevNet: number }>();
      for (const [outletId, a] of cur.outlets.entries()) {
        const city = petpoojaOutlet(outletId).city;
        const e = cityAgg.get(city) ?? { gross: 0, net: 0, bills: 0, prevNet: 0 };
        e.gross += a.gross;
        e.net += a.net;
        e.bills += a.bills;
        e.prevNet += prev.outlets.get(outletId)?.net ?? 0;
        cityAgg.set(city, e);
      }
      const cities: CityRow[] = Array.from(cityAgg.entries())
        .map(([city, e]) => ({
          city,
          gross: e.gross,
          net: e.net,
          bills: e.bills,
          netMetric: computeMetric(e.net, e.prevNet),
        }))
        .filter((c) => c.net > 0)
        .sort((a, b) => b.gross - a.gross);

      // by_channel / by_source hold pre-discount item value; scale them to
      // post-discount so channel & aggregator ₹ reconcile with net sales.
      // (Discount isn't split per channel in the feed, so allocate uniformly.)
      const discFactor = cur.totals.core > 0 ? cur.totals.net / cur.totals.core : 1;

      const channelTotal = Array.from(cur.channel.values()).reduce((x, y) => x + y, 0);
      const channelMix: MixRow[] = Array.from(cur.channel.entries())
        .map(([key, sales]) => ({
          key,
          sales: sales * discFactor,
          sharePct: channelTotal > 0 ? (sales / channelTotal) * 100 : 0,
        }))
        .sort((a, b) => b.sales - a.sales);

      let zomato = 0;
      let swiggy = 0;
      const perOutletSource = Array.from(cur.outlets.entries())
        .map(([outletId, a]) => {
          zomato += a.zomato * discFactor;
          swiggy += a.swiggy * discFactor;
          return {
            outletId,
            name: petpoojaOutlet(outletId).name,
            zomato: a.zomato * discFactor,
            swiggy: a.swiggy * discFactor,
          };
        })
        .filter((o) => o.zomato + o.swiggy > 0)
        .sort((a, b) => b.zomato + b.swiggy - (a.zomato + a.swiggy));
      const aggTotal = zomato + swiggy;

      return {
        brand,
        outlets,
        cities,
        channelMix,
        aggregator: {
          zomato,
          swiggy,
          total: aggTotal,
          zomatoPct: aggTotal > 0 ? (zomato / aggTotal) * 100 : 0,
        },
        perOutletSource,
        weekly: buildWeekly(curRows, current),
        current,
        previous,
      };
    },
    staleTime: 60_000,
  });

  return { ...query, current, previous };
}

// ── Menu: top products + category contributions ──────────────────────────────
// Sourced from marketing.pos_menu_monthly / pos_category_monthly (monthly
// granularity), so item & category numbers reflect the month(s) the selected
// period falls in.

export interface PosItemLine {
  itemId: string;
  name: string;
  categoryName: string | null;
  units: number;
  revenue: number;
  metric: Metric;
}

export interface PosCategoryLine {
  category: string;
  units: number;
  revenue: number;
  sharePct: number;
}

interface MenuRow {
  item_name: string;
  category: string | null;
  units: number | string | null;
  revenue: number | string | null;
}

/** Distinct 'YYYY-MM' month keys (IST) spanned by a date range. */
function monthsOf(range: DateRange): string[] {
  const startStr = toISTDateString(range.start);
  const endStr = toISTDateString(new Date(range.end.getTime() - DAY_MS));
  let y = Number(startStr.slice(0, 4));
  let m = Number(startStr.slice(5, 7));
  const ey = Number(endStr.slice(0, 4));
  const em = Number(endStr.slice(5, 7));
  const out: string[] = [];
  while (y < ey || (y === ey && m <= em)) {
    out.push(`${y}-${String(m).padStart(2, '0')}`);
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return out.length ? out : [startStr.slice(0, 7)];
}

async function fetchMenuItems(months: string[]): Promise<MenuRow[]> {
  const { data, error } = await marketing
    .from('pos_menu_monthly')
    .select('item_name, category, units, revenue')
    .in('mon', months)
    .order('revenue', { ascending: false })
    .limit(400);
  if (error) throw error;
  return (data ?? []) as unknown as MenuRow[];
}

export function usePosMenu({ period, limit = 8 }: { period: PeriodKey; limit?: number }) {
  const { customRange } = useDashboard();
  const current = effectivePeriodRange(period, customRange);
  const previous = getCompareRange(current, 'previous');
  const curMonths = monthsOf(current);
  const prevMonths = monthsOf(previous);

  return useQuery<{ items: PosItemLine[]; categories: PosCategoryLine[] }>({
    queryKey: ['pos-menu', curMonths.join(','), prevMonths.join(','), limit],
    queryFn: async () => {
      const [curRows, prevRows, catRows] = await Promise.all([
        fetchMenuItems(curMonths),
        fetchMenuItems(prevMonths),
        marketing
          .from('pos_category_monthly')
          .select('category, units, revenue')
          .in('mon', curMonths)
          .then(({ data, error }) => {
            if (error) throw error;
            return (data ?? []) as unknown as Array<{
              category: string;
              units: number | string | null;
              revenue: number | string | null;
            }>;
          }),
      ]);

      const aggItems = (rows: MenuRow[]) => {
        const m = new Map<string, { name: string; category: string | null; units: number; revenue: number }>();
        for (const r of rows) {
          const e = m.get(r.item_name) ?? { name: r.item_name, category: r.category, units: 0, revenue: 0 };
          e.units += n(r.units);
          e.revenue += n(r.revenue);
          m.set(r.item_name, e);
        }
        return m;
      };
      const cur = aggItems(curRows);
      const prev = aggItems(prevRows);
      const items: PosItemLine[] = Array.from(cur.values())
        .map((c) => ({
          itemId: c.name,
          name: c.name,
          categoryName: c.category,
          units: c.units,
          revenue: c.revenue,
          metric: computeMetric(c.revenue, prev.get(c.name)?.revenue ?? 0),
        }))
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, limit);

      const catMap = new Map<string, { units: number; revenue: number }>();
      for (const r of catRows) {
        const e = catMap.get(r.category) ?? { units: 0, revenue: 0 };
        e.units += n(r.units);
        e.revenue += n(r.revenue);
        catMap.set(r.category, e);
      }
      const catTotal = Array.from(catMap.values()).reduce((x, y) => x + y.revenue, 0);
      const categories: PosCategoryLine[] = Array.from(catMap.entries())
        .map(([category, e]) => ({
          category,
          units: e.units,
          revenue: e.revenue,
          sharePct: catTotal > 0 ? (e.revenue / catTotal) * 100 : 0,
        }))
        .sort((a, b) => b.revenue - a.revenue);

      return { items, categories };
    },
    staleTime: 60_000,
  });
}
